import React, { useState, useEffect } from "react";
import { SELECTED_ELEMENT_COLOR } from "../configuratorConstants";
import { getElementByCode } from "../../api/elementService";

const CustomElementRenderer = ({
  element,
  scale,
  wallThickness,
  isSelected,
  placementSideFactor,
}) => {
  const [elementData, setElementData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadElement = async () => {
      try {
        // Загружаем данные элемента из БД по elementCode (который хранится в element.type)
        const data = await getElementByCode(element.type);
        setElementData(data);
        setLoading(false);
      } catch (error) {
        console.error(`❌ Ошибка загрузки кастомного элемента ${element.type}:`, error);
        setLoading(false);
      }
    };

    loadElement();
  }, [element.type]);

  if (loading || !elementData) {
    // Показываем placeholder пока загружается
    return (
      <rect
        x={-10}
        y={-10}
        width={20}
        height={20}
        fill="rgba(200, 200, 200, 0.5)"
        stroke="rgba(100, 100, 100, 0.7)"
        strokeWidth={1}
      />
    );
  }

  if (!elementData.svgPath) {
    console.error(`❌ Нет svgPath для элемента ${element.type}`);
    return null;
  }

  // URL SVG файла
  const svgUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${elementData.svgPath}`;

  // Размеры элемента из БД (или дефолтные)
  const elementWidthM = elementData.width || element.width || 1.0;
  const elementHeightM = elementData.height || element.height || 1.0;

  // sandwichCount определяет сколько ячеек модуля займет элемент
  // Это используется для размещения, но отрисовка идет по width
  const sandwichCount = elementData.sandwichCount || element.sandwichCount || 1;
  const GRID_CELL_SIZE_M = 1.0; // Размер одной ячейки модуля

  // Фактическая область которую занимает элемент на модуле
  const occupiedWidthM = sandwichCount * GRID_CELL_SIZE_M;

  const elementWidthPx = elementWidthM * scale;
  const elementHeightPx = elementHeightM * scale;
  const occupiedWidthPx = occupiedWidthM * scale;

  // Применяем transform rules если есть
  let transformStr = "";
  if (elementData.transformRules) {
    const rules = typeof elementData.transformRules === 'string'
      ? JSON.parse(elementData.transformRules)
      : elementData.transformRules;

    if (rules.rotate) {
      transformStr += `rotate(${rules.rotate}) `;
    }
    if (rules.scale) {
      transformStr += `scale(${rules.scale}) `;
    }
    if (rules.translate) {
      transformStr += `translate(${rules.translate.x || 0}, ${rules.translate.y || 0}) `;
    }
  }

  // Центрируем элемент с учетом defaultPosition
  const defaultPosX = elementData.defaultPositionX || 0;
  const defaultPosY = elementData.defaultPositionY || 0;
  const defaultRotation = elementData.defaultRotation || 0;

  const xOffset = -elementWidthPx / 2 + defaultPosX * scale;
  const yOffset = -elementHeightPx / 2 + defaultPosY * scale;

  // Если элемент является дверью, применяем логику двери (точно как в DoorRenderer)
  const isDoor = elementData.isDoor || false;
  const openingDirection = element.openingDirection || 'outward';
  const hingeSide = element.hingeSide || 'left';

  // Центр элемента для вращения
  const centerX = xOffset + elementWidthPx / 2;
  const centerY = yOffset + elementHeightPx / 2;

  // Добавляем поворот к трансформации
  // Вращаем вокруг центра элемента
  const rotationTransform = defaultRotation !== 0 ? `rotate(${defaultRotation}, ${centerX}, ${centerY})` : '';

  // Логика зеркалирования для дверей
  let doorScaleTransform = '';
  if (isDoor) {
    const scaleX = hingeSide === 'right' ? -1 : 1;
    const scaleY = openingDirection === 'inward' ? -1 : 1;

    if (scaleX !== 1 || scaleY !== 1) {
      // Масштабирование относительно центра элемента
      doorScaleTransform = `translate(${centerX}, ${centerY}) scale(${scaleX}, ${scaleY}) translate(${-centerX}, ${-centerY})`;
    }
  }

  console.log(`🎨 Рендерим кастомный элемент "${elementData.elementName}":`, {
    elementCode: element.type,
    svgUrl,
    widthFromDB: elementData.width,
    heightFromDB: elementData.height,
    sandwichCount: sandwichCount,
    occupiedWidthM: occupiedWidthM,
    displayWidthM: elementWidthM,
    defaultPositionX: elementData.defaultPositionX,
    defaultPositionY: elementData.defaultPositionY,
    defaultRotation: elementData.defaultRotation,
    scale,
    transformStr,
    isDoor,
    rotationTransform,
    doorScaleTransform,
    openingDirection,
    hingeSide
  });

  return (
    <g transform={transformStr}>
      <g transform={rotationTransform}>
        {/* Применяем зеркалирование для дверей */}
        <g transform={doorScaleTransform}>
          {/* Область клика - всегда рендерится для перехвата событий */}
          <rect
            x={xOffset}
            y={yOffset}
            width={elementWidthPx}
            height={elementHeightPx}
            fill="transparent"
            stroke="none"
            style={{ pointerEvents: 'all', cursor: 'pointer' }}
          />

          {/* Занимаемое пространство (если sandwichCount > 1) - скрыто, показываем только при выделении для отладки */}
          {sandwichCount > 1 && isSelected && (
            <rect
              x={-occupiedWidthPx / 2}
              y={yOffset}
              width={occupiedWidthPx}
              height={elementHeightPx}
              fill="rgba(255, 165, 0, 0.1)"
              stroke="rgba(255, 165, 0, 0.3)"
              strokeWidth={1}
              strokeDasharray="3,3"
            />
          )}

          {/* Фон элемента - показываем только при выделении для визуальной обратной связи */}
          {isSelected && (
            <rect
              x={xOffset}
              y={yOffset}
              width={elementWidthPx}
              height={elementHeightPx}
              fill="rgba(0, 123, 255, 0.2)"
              stroke={SELECTED_ELEMENT_COLOR}
              strokeWidth={2}
              strokeDasharray="5,5"
            />
          )}

          {/* SVG элемента */}
          <image
            href={svgUrl}
            x={xOffset}
            y={yOffset}
            width={elementWidthPx}
            height={elementHeightPx}
            preserveAspectRatio="none"
            opacity={isSelected ? 0.9 : 1}
            style={{
              pointerEvents: 'none',
              fill: isSelected ? "rgba(0, 123, 255, 0.15)" : "rgba(220, 220, 225, 0.85)",
              stroke: isSelected ? SELECTED_ELEMENT_COLOR : "rgba(60, 70, 80, 0.95)",
            }}
          />

        </g>

        {/* Метка количества сендвижей */}
        {sandwichCount > 1 && (
          <text
            x={centerX}
            y={yOffset + elementHeightPx + 15}
            fontSize="10"
            fill="#ff8c00"
            textAnchor="middle"
            fontWeight="bold"
          >
            {sandwichCount} 🥪
          </text>
        )}
      </g>
    </g>
  );
};

export default React.memo(CustomElementRenderer);

